import { FC, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { cn } from "@/utils/tailwind-utils";
import MaatraLogo from "@/components/ui/atoms/MaatraLogo";
import { Avatar, AvatarFallback } from "@/components/ui/atoms/avatar";
import { User, LogOut, ChevronDown } from "lucide-react";
import useAdminNavigation from "@/hooks/useAdminNavigation";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import adminNavigation from "@/config/navigation/admin-navigation.json";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/molecules/dropdown-menu";

const getPageTitle = (pathname: string): string => {
  const match = Object.values(
    adminNavigation as Record<string, { label: string; route: string }>
  ).find((item) => item.route === pathname);
  return match?.label ?? "Admin";
};

export const AdminLayout: FC = () => {
  const location = useLocation();
  const { user, logout } = useAdminAuth();
  const { navigationSections, navigateTo } = useAdminNavigation();
  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    document.body.classList.add("page-fullwidth");
    return () => document.body.classList.remove("page-fullwidth");
  }, []);

  return (
    <div className={cn("flex h-screen overflow-hidden", "bg-white")}>
      {/* Left Sidebar */}
      <aside className="w-64 border-r border-gray-200 flex flex-col h-screen shrink-0">
        <div className="h-16 border-b border-gray-200 flex items-center px-4 gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#4c189a] flex-shrink-0">
            <MaatraLogo fill="#fff" size={20} />
          </div>
          <span className="font-bold text-lg text-[#3d1379] tracking-tight">
            Maatra
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {Object.entries(navigationSections).map(([key, section]) => {
            if (section.items.length === 0) return null;
            return (
              <div key={key} className="mb-6">
                <h3 className="text-xs uppercase text-gray-500 font-semibold px-3 mb-2">
                  {section.title}
                </h3>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.label}>
                      <button
                        onClick={() => navigateTo(item.route)}
                        className={cn(
                          "flex w-full items-center px-3 py-2 rounded-md transition-colors text-left",
                          location.pathname === item.route
                            ? "bg-[#EDE8F5] text-[#3d1379] font-medium"
                            : "text-gray-700"
                        )}
                      >
                        {item.icon && <item.icon className="w-5 h-5 mr-3" />}
                        <span>{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b border-gray-200 flex items-center justify-between px-6">
          <h1 className="text-xl font-semibold text-gray-800">{pageTitle}</h1>

          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback>
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">
                {user?.name ?? "Admin"}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {user?.email && (
                <div className="px-3 py-2 text-xs text-gray-500 border-b">
                  {user.email}
                </div>
              )}
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-auto overflow-x-hidden p-6 bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
